import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, onSnapshot, orderBy, limit, doc, updateDoc } from 'firebase/firestore';
import {
  Box,
  Paper,
  Typography,
  Chip,
  LinearProgress,
  IconButton,
  Stack,
  Divider
} from '@mui/material';
import {
  CheckBoxOutlineBlank,
  CheckCircle,
  TrendingUp,
  EmojiEvents,
  AssignmentTurnedIn,
  Person,
  East,
  Today,
  Schedule,
  History
} from '@mui/icons-material';
import { db } from './firebase';
import { useAuth } from './AuthContext';
import { STAGES, ACTIVITY_LABELS } from './constants';
import { fmtDate, fmtDateTime, daysDiff } from './helpers';

export default function Dashboard() {
  const { staff } = useAuth();
  const navigate = useNavigate();
  const [contatti, setContatti] = useState([]);
  const [attivita, setAttivita] = useState([]);
  const [task, setTask] = useState([]);

  const isAdmin = staff?.role === 'admin';

  useEffect(() => {
    const unsubs = [
      onSnapshot(collection(db, 'crm_contatti'), s =>
        setContatti(s.docs.map(d => ({ id: d.id, ...d.data() })))
      ),
      onSnapshot(
        query(collection(db, 'crm_attivita'), orderBy('data', 'desc'), limit(30)),
        s => setAttivita(s.docs.map(d => ({ id: d.id, ...d.data() })))
      ),
      onSnapshot(collection(db, 'crm_task'), s =>
        setTask(s.docs.map(d => ({ id: d.id, ...d.data() })))
      )
    ];
    return () => unsubs.forEach(u => u());
  }, []);

  // Visibilità task: pubblici a tutti, privati solo all'owner
const visibleTasks = useMemo(
  () => task.filter(t => {
    if (!t.private) return true;
    return t.owner === staff?.nome;
  }),
  [task, staff?.nome]
);

  const visibleAttivita = useMemo(
    () => isAdmin ? attivita : attivita.filter(a => a.owner === staff?.nome),
    [attivita, isAdmin, staff?.nome]
  );

  const stats = useMemo(() => ({
    tot: contatti.length,
    pipeline: contatti.filter(c => ['nuovo', 'contattato', 'demo', 'trattativa'].includes(c.stage)).length,
    vinti: contatti.filter(c => c.stage === 'vinto').length,
    oggi: visibleTasks.filter(t => !t.fatto && daysDiff(t.scadenza) !== null && daysDiff(t.scadenza) <= 0).length,oggi: visibleTasks.filter(t => !t.fatto && t.owner === staff?.nome && daysDiff(t.scadenza) !== null && daysDiff(t.scadenza) <= 0).length,
    miei: visibleTasks.filter(t => !t.fatto && t.owner === staff?.nome).length
  }), [contatti, visibleTasks, staff?.nome]);

  const todayTasks = useMemo(
    () =>
      visibleTasks
        .filter(t => !t.fatto && daysDiff(t.scadenza) !== null && daysDiff(t.scadenza) <= 0)
        .sort((a, b) => (a.scadenza || '').localeCompare(b.scadenza || '')),
    [visibleTasks]
  );

  // Attività future entro 7 giorni (per card "Prossimi 7 giorni")
  const upcomingActivities = useMemo(() => {
    const now = new Date();
    const in7days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return visibleAttivita
      .filter(a => {
        if (!a.data) return false;
        const d = new Date(a.data);
        return d > now && d <= in7days;
      })
      .sort((a, b) => (a.data || '').localeCompare(b.data || ''))
      .slice(0, 5);
  }, [visibleAttivita]);

  // Attività già avvenute (per card "Ultime attività")
  const pastActivities = useMemo(() => {
    const now = new Date();
    return visibleAttivita
      .filter(a => {
        if (!a.data) return false;
        return new Date(a.data) <= now;
      })
      .slice(0, 5);
  }, [visibleAttivita]);

  const toggleTask = async (t) => {
    await updateDoc(doc(db, 'crm_task', t.id), {
      fatto: !t.fatto,
      completedAt: !t.fatto ? new Date().toISOString() : null
    });
  };

  const openContact = (id) => navigate('/contatti?id=' + id);

  const kpis = [
    {
      label: 'Contatti totali',
      value: stats.tot,
      icon: <Person sx={{ fontSize: 18 }} />,
      accent: '#2563eb'
    },
    {
      label: 'In pipeline',
      value: stats.pipeline,
      icon: <TrendingUp sx={{ fontSize: 18 }} />,
      accent: '#7c3aed'
    },
    {
      label: 'Clienti vinti',
      value: stats.vinti,
      icon: <EmojiEvents sx={{ fontSize: 18 }} />,
      accent: '#10b981'
    },
    {
      label: isAdmin ? 'Task oggi' : 'Miei task oggi',
      value: stats.oggi,
      icon: <AssignmentTurnedIn sx={{ fontSize: 18 }} />,
      accent: '#f59e0b'
    },
    {
      label: `Task di ${staff?.nome || 'Mario'}`,
      value: stats.miei,
      icon: <Today sx={{ fontSize: 18 }} />,
      accent: '#0f172a'
    }
  ];

  const stageCounts = STAGES.map(s => {
    const n = contatti.filter(c => c.stage === s.id).length;
    const pct = stats.tot > 0 ? (n / stats.tot) * 100 : 0;
    return { ...s, n, pct };
  });

  const shellBg = '#f3f6fb';
  const cardBorder = '1px solid rgba(15,23,42,0.08)';
  const cardShadow = '0 10px 30px rgba(15,23,42,0.06)';
  const radius = 4;

  return (
    <Box sx={{ bgcolor: shellBg, minHeight: '100%', p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.25, md: 3 },
          borderRadius: radius,
          color: 'white',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #2563eb 100%)',
          boxShadow: '0 18px 40px rgba(15,23,42,0.18)'
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: -0.5, mb: 0.5 }}>
              Ciao {staff?.nome || 'Mario'}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.82)', maxWidth: 720 }}>
              Dashboard operativa AUCTORIS. Qui devi vedere subito cosa richiede attenzione, cosa è fermo e dove sta girando la pipeline.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label={`${stats.oggi} task urgenti`}
              sx={{
                bgcolor: 'rgba(255,255,255,0.14)',
                color: 'white',
                fontWeight: 700,
                border: '1px solid rgba(255,255,255,0.18)'
              }}
            />
            <Chip
              label={`${stats.pipeline} in lavorazione`}
              sx={{
                bgcolor: 'rgba(255,255,255,0.14)',
                color: 'white',
                fontWeight: 700,
                border: '1px solid rgba(255,255,255,0.18)'
              }}
            />
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(5, minmax(0, 1fr))'
          },
          gap: 2,
          mb: 3
        }}
      >
        {kpis.map(k => (
          <Paper
            key={k.label}
            elevation={0}
            sx={{
              p: 2,
              borderRadius: radius,
              border: cardBorder,
              boxShadow: cardShadow,
              bgcolor: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: 6,
                height: '100%',
                bgcolor: k.accent
              }}
            />
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box sx={{ pl: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                  {k.label}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1 }}>
                  {k.value}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: `${k.accent}15`,
                  color: k.accent
                }}
              >
                {k.icon}
              </Box>
            </Stack>
          </Paper>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', xl: '1.25fr 0.95fr' },
          gap: 2
        }}
      >
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.25,
              borderRadius: radius,
              border: cardBorder,
              boxShadow: cardShadow,
              bgcolor: 'white'
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Da fare oggi
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Task scaduti o in scadenza oggi
                </Typography>
              </Box>
              <Chip
                label={todayTasks.length}
                sx={{
                  bgcolor: todayTasks.length ? '#fee2e2' : '#e2e8f0',
                  color: todayTasks.length ? '#b91c1c' : '#334155',
                  fontWeight: 800,
                  minWidth: 40
                }}
              />
            </Stack>

            {todayTasks.length === 0 ? (
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  bgcolor: '#f8fafc',
                  border: '1px dashed rgba(15,23,42,0.12)'
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Tutto sotto controllo.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Oggi non hai task urgenti o scaduti.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.25}>
                {todayTasks.map(t => {
                  const c = contatti.find(x => x.id === t.contattoId);
                  const diff = daysDiff(t.scadenza);
                  const overdue = diff < 0;

                  return (
                    <Box
                      key={t.id}
                      sx={{
                        p: 1.4,
                        borderRadius: 3,
                        border: overdue
                          ? '1px solid rgba(239,68,68,0.18)'
                          : '1px solid rgba(15,23,42,0.08)',
                        bgcolor: overdue ? 'rgba(254,242,242,0.9)' : '#f8fafc',
                        transition: '0.18s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 8px 20px rgba(15,23,42,0.06)'
                        }
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="flex-start">
                        <IconButton
                          size="small"
                          onClick={() => toggleTask(t)}
                          sx={{
                            mt: 0.15,
                            bgcolor: 'white',
                            border: '1px solid rgba(15,23,42,0.08)',
                            '&:hover': { bgcolor: 'white' }
                          }}
                        >
                          <CheckBoxOutlineBlank fontSize="small" />
                        </IconButton>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body1" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {t.titolo}
                          </Typography>

                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                            <Chip
                              size="small"
                              label={overdue ? `${Math.abs(diff)}g in ritardo` : 'Oggi'}
                              sx={{
                                bgcolor: overdue ? '#fecaca' : '#e2e8f0',
                                color: overdue ? '#991b1b' : '#334155',
                                fontWeight: 700
                              }}
                            />
                            <Chip
                              size="small"
                              label={t.owner || 'N/D'}
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                          </Stack>

                          {c && (
                            <Typography
                              variant="body2"
                              onClick={() => openContact(c.id)}
                              sx={{
                                color: '#2563eb',
                                cursor: 'pointer',
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              {c.nome}
                              <East sx={{ fontSize: 14 }} />
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Paper>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
              gap: 2
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2.25,
                borderRadius: radius,
                border: cardBorder,
                boxShadow: cardShadow,
                bgcolor: 'white'
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Prossimi 7 giorni
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Attività programmate a breve
                  </Typography>
                </Box>
                <Schedule sx={{ color: '#475569' }} />
              </Stack>

              {upcomingActivities.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  Niente in programma.
                </Typography>
              ) : (
                <Stack spacing={1.25}>
                  {upcomingActivities.map(a => {
                    const c = contatti.find(x => x.id === a.contattoId);
                    return (
                      <Box
                        key={a.id}
                        sx={{
                          p: 1.2,
                          borderRadius: 3,
                          bgcolor: '#f8fafc',
                          border: '1px solid rgba(15,23,42,0.08)'
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {ACTIVITY_LABELS[a.tipo] || a.tipo}
                          {c ? ` · ${c.azienda || c.nome}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fmtDateTime(a.data)} · {a.owner}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 2.25,
                borderRadius: radius,
                border: cardBorder,
                boxShadow: cardShadow,
                bgcolor: 'white'
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Ultime attività
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ultimi movimenti registrati nel CRM
                  </Typography>
                </Box>
                <History sx={{ color: '#475569' }} />
              </Stack>

              {pastActivities.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  Nessuna attività registrata.
                </Typography>
              ) : (
                <Stack spacing={1.2}>
                  {pastActivities.map((a, idx) => {
                    const c = contatti.find(x => x.id === a.contattoId);

                    return (
                      <Box key={a.id}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {ACTIVITY_LABELS[a.tipo]}
                          {a.esito ? ` · ${a.esito}` : ''}
                        </Typography>

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.35 }}>
                          {c && (
                            <Typography
                              variant="caption"
                              onClick={() => openContact(c.id)}
                              sx={{
                                color: '#2563eb',
                                cursor: 'pointer',
                                fontWeight: 600,
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              {c.nome}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {fmtDateTime(a.data)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {a.owner}
                          </Typography>
                        </Stack>

                        {idx < pastActivities.length - 1 && <Divider sx={{ mt: 1.2 }} />}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2.25,
            borderRadius: radius,
            border: cardBorder,
            boxShadow: cardShadow,
            bgcolor: 'white',
            alignSelf: 'start'
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Pipeline
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Distribuzione attuale dei contatti
              </Typography>
            </Box>
            <Chip
              label={`${stats.tot} totali`}
              variant="outlined"
              sx={{ fontWeight: 700 }}
            />
          </Stack>

          <Stack spacing={1.4}>
            {stageCounts.map(stage => (
              <Box
                key={stage.id}
                sx={{
                  p: 1.25,
                  borderRadius: 3,
                  bgcolor: '#f8fafc',
                  border: '1px solid rgba(15,23,42,0.06)'
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: stage.color
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {stage.label}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {stage.n}
                  </Typography>
                </Stack>

                <LinearProgress
                  variant="determinate"
                  value={stage.pct}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    bgcolor: 'rgba(148,163,184,0.22)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: stage.color,
                      borderRadius: 999
                    }
                  }}
                />

                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                  {stats.tot > 0 ? `${stage.pct.toFixed(1)}% del totale` : 'Nessun dato'}
                </Typography>
              </Box>
            ))}
          </Stack>

          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 3,
              bgcolor: '#eff6ff',
              border: '1px solid rgba(37,99,235,0.12)'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1d4ed8', mb: 0.35 }}>
              Focus rapido
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Hai <strong>{stats.pipeline}</strong> contatti in lavorazione e <strong>{stats.vinti}</strong> già chiusi.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}